import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, Checkbox, Divider,
} from '@material-ui/core';
import { usePostHog, useFeatureFlagVariantKey } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { ContentContainer } from './AccessCodeEntry.styles';
import {
  FlexContainer,
  HeaderBox,
  Title,
  RequiredConsentLabel,
  OptionalConsentLabel,
  continueButtonStyles,
  DividerContainer,
  DividerText,
} from './CreateAccount.styles';

import { EMPLOYER_ORG_SLUG_KEY } from '@/utils/constants';
// eslint-disable-next-line max-len
import DependentEligibilityCheck from '@/pages/PreOnboarding/steps/EmployerDependentSelection/DependentEligibilityCheck';

import EmailIcon from '@/assets/Email.svg';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import SocialButton from '@/components/SocialButton/SocialButton';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product, getLob } from '@/utils/productHelpers';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkPhone } from '@/utils/sparkActivationApi';
import { ActiveFeatureFlag } from '@/hooks/usePostHogFeatureFlags';
import { logError } from '@/utils/logError';

const schema = z.object({
  phoneNumber: z.string().refine(
    (v) => isValidPhoneNumber(v || ''),
    { message: 'coverage.sleepio.patient.please_enter_valid_phone_number' },
  ),
  readAndAcceptTerms: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
});

type CreateAccountForm = z.infer<typeof schema>;

const PHONE_ERROR_MESSAGE = 'coverage.sleepio.patient.please_enter_valid_phone_number';

interface CreateAccountLocationState {
  accessCode?: string;
  limbixUserId?: string;
  authMethods?: string[];
}

const CreateAccountInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  const history = useHistory();
  const { state: locationState, search } = useLocation<CreateAccountLocationState>();
  const enrollment = new URLSearchParams(search).get('enrollment') ?? undefined;
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { product, org_slug: orgSlug } = useParams<{ product: Product; org_slug?: string }>();
  const isEmployerFlow = !!orgSlug;

  // Auth methods from access code config. Empty/undefined = show both (default).
  const authMethods = locationState?.authMethods ?? [];
  const showPhone = authMethods.length === 0 || authMethods.includes('PHONE');
  const showEmail = authMethods.length === 0 || authMethods.includes('EMAIL');

  // Back navigation is blocked for the access-code flow (single-use code).
  // For employer flow, back should return to the dependent selection screen.
  useBlockBackNavigation(!isEmployerFlow);

  const methods = useForm<CreateAccountForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: '',
      readAndAcceptTerms: false,
      marketingConsent: false,
    },
  });
  const {
    handleSubmit, control, formState, watch,
  } = methods;
  const readAndAcceptTermsValue = watch('readAndAcceptTerms');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const sparkAuthFlag = useFeatureFlagVariantKey(ActiveFeatureFlag.SPARK_AUTH);

  // Employer flow guards: sessionStorage validation + feature flag check.
  useEffect(() => {
    if (!isEmployerFlow) { return; }

    const storedSlug = sessionStorage.getItem(EMPLOYER_ORG_SLUG_KEY);
    if (storedSlug !== orgSlug && orgSlug) {
      sessionStorage.setItem(EMPLOYER_ORG_SLUG_KEY, orgSlug);
    }

    if (sparkAuthFlag === undefined) { return; }
    if (sparkAuthFlag !== 'enroll_type') {
      history.replace('/activate/spark/employers');
    }
  }, [isEmployerFlow, orgSlug, history, sparkAuthFlag]);

  useEffect(() => {
    posthog?.capture('SparkCreateAccountScreenViewed');
    posthog?.capture('SignUpMethodLanded');
  }, [posthog]);

  const onSubmit = async (data: CreateAccountForm) => {
    posthog?.capture('SparkCreateAccountContinueClicked');
    posthog?.register({ auth_method: 'phone' });
    posthog?.capture('SignUpMethodClicked');
    posthog?.capture('SignUpMethodSubmitted', {
      phone_number: data.phoneNumber,
      input: data.phoneNumber,
    });
    if (data.marketingConsent) {
      posthog?.capture('MarketingConsented', {
        phone_number: data.phoneNumber,
        input: data.phoneNumber,
      });
    }
    setIsSending(true);
    setSendError('');

    if (isEmployerFlow) {
      let limbixUserId: string | undefined;
      try {
        const activationResult = await activateSparkPhone({
          phone: data.phoneNumber,
          organizationSlug: orgSlug,
          lob: getLob(orgSlug),
          phDistinctId: posthog?.get_distinct_id(),
        });
        limbixUserId = activationResult.limbix_user_id;
      } catch (err) {
        logError('[CreateAccount]', 'Employer /activate/phone failed', err);
        setSendError(t('activate.employers.create_account.error_send_failed'));
        setIsSending(false);
        return;
      }

      const { error: otpError } = await getSupabaseClient().auth.signInWithOtp({
        phone: data.phoneNumber,
      });

      if (otpError) {
        setSendError(t('activate.employers.create_account.error_send_failed'));
        setIsSending(false);
        return;
      }

      history.push(`/activate/${product}/sms/verify`, {
        phoneNumber: data.phoneNumber,
        marketingConsent: data.marketingConsent,
        limbixUserId,
        enrollment,
        orgSlug,
      });
      return;
    }

    // Access-code flow: validate phone against Cortex before triggering OTP.
    if (!locationState?.accessCode) {
      setSendError(t('coverage.pre_onboarding.create_account.error_send_failed'));
      setIsSending(false);
      return;
    }

    let limbixUserId: string | undefined;
    try {
      const activationResult = await activateSparkPhone({
        phone: data.phoneNumber,
        accessCode: locationState.accessCode,
        product,
        lob: getLob(orgSlug),
        phDistinctId: posthog?.get_distinct_id(),
      });
      limbixUserId = activationResult.limbix_user_id;
    } catch (err) {
      logError('[CreateAccount]', 'Step 1 /activate/phone failed', err);
      setSendError(t('coverage.pre_onboarding.create_account.error_send_failed'));
      setIsSending(false);
      return;
    }

    const { error: otpError } = await getSupabaseClient().auth.signInWithOtp({
      phone: data.phoneNumber,
    });

    if (otpError) {
      setSendError(t('coverage.pre_onboarding.create_account.error_send_failed'));
      setIsSending(false);
      return;
    }

    history.push(`/activate/${product}/sms/verify`, {
      phoneNumber: data.phoneNumber,
      marketingConsent: data.marketingConsent,
      limbixUserId,
      accessCode: locationState.accessCode,
    });
  };

  const handleSocialButtonClick = (provider: string) => {
    posthog?.capture(`SparkContinueWith${provider}Clicked`);
    posthog?.register({ auth_method: provider.toLowerCase() });
    posthog?.capture('SignUpMethodClicked');
    if (provider !== 'Email') { return; }

    const emailState = isEmployerFlow
      ? { organizationSlug: orgSlug }
      : { ...(locationState?.accessCode && { accessCode: locationState.accessCode }) };

    history.push(`/activate/${product}/email-auth`, emailState);
  };

  const titleKey = isEmployerFlow
    ? 'activate.employers.create_account.title'
    : 'coverage.pre_onboarding.create_account.title';

  const subTitleKey = isEmployerFlow
    ? 'activate.employers.create_account.sub_title'
    : 'coverage.pre_onboarding.create_account.sub_title';

  const marketingConsentKey = isEmployerFlow
    ? 'activate.employers.create_account.marketing_consent'
    : 'coverage.pre_onboarding.create_account.marketing_consent';

  const continueKey = isEmployerFlow
    ? 'activate.employers.create_account.continue'
    : 'coverage.pre_onboarding.create_account.continue';

  const sendingKey = isEmployerFlow
    ? 'activate.employers.create_account.sending'
    : 'coverage.pre_onboarding.create_account.sending';

  // The employer flow adds a required Privacy Policy + Terms of Use checkbox.
  // Since it's not in the Zod schema as required, we track it via watch and
  // gate the button manually.
  const isSubmitDisabled = !formState.isValid
    || isSending
    || !readAndAcceptTermsValue;

  return (
    <ContentContainer>
      <Header />
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t(titleKey)}
          </Title>
          <Typography color="textPrimary">
            {t(subTitleKey)}
          </Typography>
        </HeaderBox>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {showPhone && (
              <>
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: t(PHONE_ERROR_MESSAGE),
                    validate: (v) => isValidPhoneNumber(v || '') || t(PHONE_ERROR_MESSAGE),
                  }}
                  render={({ field, fieldState }) => (
                    <PhoneNumberInput
                      id="phoneNumber"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('coverage.sleepio.patient.phone_label')}
                      error={!!fieldState.error}
                      helperText={fieldState.error ? t(PHONE_ERROR_MESSAGE) : undefined}
                      required
                    />
                  )}
                />

                <Controller
                  name="readAndAcceptTerms"
                  control={control}
                  render={({ field }) => (
                    <RequiredConsentLabel
                      label={(
                        <Typography>
                          {t('activate.employers.create_account.read_and_accept')}
                          {' '}
                          {t('activate.employers.create_account.privacy_policy')}
                          {' '}
                          {t('activate.employers.create_account.and')}
                          {' '}
                          {t('activate.employers.create_account.terms_of_use')}
                          *
                        </Typography>
                      )}
                      control={(
                        <Checkbox
                          {...field}
                          checked={Boolean(field.value)}
                          data-testid="consent-terms-checkbox"
                        />
                      )}
                    />
                  )}
                />

                <Controller
                  name="marketingConsent"
                  control={control}
                  render={({ field }) => (
                    <OptionalConsentLabel
                      label={(
                        <Typography>
                          {t(marketingConsentKey)}
                        </Typography>
                      )}
                      control={(
                        <Checkbox
                          {...field}
                          checked={Boolean(field.value)}
                          data-testid="consent-optional-checkbox"
                        />
                      )}
                    />
                  )}
                />

                <Button
                  variant="contained"
                  data-testid="continue-button"
                  type="submit"
                  fullWidth
                  color="primary"
                  disabled={isSubmitDisabled}
                  rounded
                  style={continueButtonStyles}
                >
                  {isSending ? t(sendingKey) : t(continueKey)}
                </Button>

                {sendError && (
                  <Typography color="error" data-testid="send-error-message" style={{ marginTop: 8 }}>
                    {sendError}
                  </Typography>
                )}
              </>
            )}

            {showEmail && (
              <>
                {showPhone && (
                  <DividerContainer>
                    <Divider />
                    <DividerText>{t('coverage.pre_onboarding.create_account.or')}</DividerText>
                  </DividerContainer>
                )}

                {/* TODO: Uncomment when Google/Apple social auth is implemented */}
                {/* <SocialButton
                  icon={<img src={GoogleIcon} alt="Google icon" width={20} height={20} />}
                  labelKey="coverage.pre_onboarding.create_account.continue_google"
                  testId="continue-google-button"
                  onClick={() => handleSocialButtonClick('Google')}
                />
                <SocialButton
                  icon={<img src={AppleIcon} alt="Apple icon" width={20} height={20} />}
                  labelKey="coverage.pre_onboarding.create_account.continue_apple"
                  testId="continue-apple-button"
                  onClick={() => handleSocialButtonClick('Apple')}
                /> */}
                <SocialButton
                  icon={<img src={EmailIcon} alt="Email icon" width={20} height={20} />}
                  labelKey="coverage.pre_onboarding.create_account.continue_email"
                  testId="continue-email-button"
                  onClick={() => handleSocialButtonClick('Email')}
                  marginBottom={0}
                />
              </>
            )}
          </form>
        </FormProvider>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

const CreateAccount: React.FC = () => {
  const { search } = useLocation();
  const enrollment = new URLSearchParams(search).get('enrollment');

  if (enrollment === 'dependent') {
    return <DependentEligibilityCheck />;
  }

  return (
    <ThemeProvider theme={sparkTheme}>
      <CreateAccountInner />
    </ThemeProvider>
  );
};

export default CreateAccount;
